import {
  BaseModal,
  Button,
  FileUploader,
  Input,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import React, { useEffect, useState } from 'react';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import Select from 'components/base/select';
import { LevelFormDataType, MealType, MultiselectOptionsType, RewardType } from 'utils/types';
import { getComplimentaryMeals, getMealNameAndImages } from 'utils/functions';
import { STORAGE_KEYS } from 'utils/constants';

type EditLevelFormDataType = LevelFormDataType & {
  id: string;
};

const EditLevelModal = ({
  show,
  onClose,
  data
}: {
  show: boolean;
  onClose: (success?: boolean) => void;
  data: EditLevelFormDataType;
}) => {
  const COMPLIMENTARY = 'comp';
  const [formData, setFormData] = useState(data);
  const [loading, setLoading] = useState(false);
  const [complimentaryMeals, setComplimentaryMeals] = useState<MealType[]>([]);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

  const onConfirm = async () => {
    setLoading(true);
    try {
      const { max_points, title, rewards, tier_image, complimentary_meals } = formData;
      const res = await apiService.put(`internal/loyalty-rewards-level/${data.id}`, {
        data: { max_points, title, rewards, tier_image, complimentary_meals },
        isInternal: true
      });
      if (res?.data) {
        toast(
          <ToastMessage
            type='success'
            message={`${formData.title} has been updated successfully`}
          />
        );
        onClose(true);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={`Error updating ${formData.title}`} />);
    } finally {
      setLoading(false);
    }
  };

  const fetchComplimentaryMeals = async () => {
    await getComplimentaryMeals(setComplimentaryMeals);
  };

  useEffect(() => {
    fetchComplimentaryMeals();
  }, []);

  const getRewardValue = (rewardType: RewardType) => {
    const rewardValue =
      formData.rewards.find((reward) => reward.reward_type === rewardType)?.reward_quantity ?? '';
    return rewardValue;
  };

  const handleRewardChange = (e: React.ChangeEvent<HTMLInputElement>, rewardType: RewardType) => {
    const rewards = [...formData.rewards];
    const rewardIndex = rewards.findIndex((reward) => reward.reward_type === rewardType);
    if (rewardIndex !== -1) {
      rewards[rewardIndex] = {
        ...rewards[rewardIndex],
        reward_quantity: parseInt(e.target.value)
      };
      setFormData({ ...formData, rewards });
    }
  };

  const mealsList: MultiselectOptionsType[] = complimentaryMeals
    .filter((meal) => meal.name.toLowerCase().startsWith(COMPLIMENTARY))
    .map((meal) => ({
      text: meal.name,
      value: meal.id
    }));
  return (
    <div>
      <BaseModal
        isOpen={show}
        title={`Edit ${formData.title}`}
        onRequestClose={() => onClose()}
        footer={
          <Button
            onClick={onConfirm}
            color='primary'
            variant='block'
            loading={loading}
            disabled={loading}
            small>
            Confirm
          </Button>
        }>
        <div className='grid grid-cols-2 gap-2.5'>
          <Input
            readOnly
            small
            label='Tier Name'
            type='text'
            placeholder='Tier 1 - Foodie'
            className='w-full mt-6 text-sm'
            inputClass='px-[20px] py-[14px]'
            value={formData?.title}
          />
          <Input
            small
            readOnly
            label='FC Points Threshold'
            type='number'
            placeholder='20,000'
            className='w-full mt-6 text-sm mb-3'
            inputClass='px-[20px] py-[14px]'
            value={formData.max_points ?? ''}
          />

          <Input
            small
            label='Order Discount (%)'
            type='number'
            placeholder='e.g 10'
            className='w-full text-sm mb-3'
            inputClass='px-[20px] py-[14px]'
            value={getRewardValue('Discount')}
            onChange={(e) => handleRewardChange(e, 'Discount')}
            onWheel={(e) => e.currentTarget.blur()}
          />
          <Input
            small
            label='Free Deliveries'
            type='number'
            placeholder='e.g 2'
            className='w-full text-sm mb-3'
            inputClass='px-[20px] py-[14px]'
            value={getRewardValue('Free Delivery')}
            onWheel={(e) => e.currentTarget.blur()}
            onChange={(e) => handleRewardChange(e, 'Free Delivery')}
          />
        </div>
        <div className='mb-2'>
          <FileUploader
            token={token}
            fileUrls={formData.tier_image ? [formData.tier_image] : []}
            label={<h3 className='font-medium mb-2'> Upload image </h3>}
            setFileUrls={(k) => setFormData({ ...formData, tier_image: k[0] ?? null })}
          />
        </div>

        <div className='mb-[24px]'>
          <p className='font-medium text-sm'>Complimentary Item</p>
          <Select
            multiple
            hasSearch
            placeholder='Select Complimentary Item'
            values={formData.complimentary_meals.map((m) => m.id)}
            options={mealsList}
            onChange={(e) =>
              setFormData({
                ...formData,
                complimentary_meals: e.map((id: string) => ({
                  id: id,
                  ...getMealNameAndImages(id, complimentaryMeals)
                }))
              })
            }
          />
        </div>
      </BaseModal>
    </div>
  );
};

export default EditLevelModal;
